import { matchPath } from "react-router";

import { faro } from "@grafana/faro-react";
import { QueryClientInstance } from "@vega-api/configs/QueryClient";
import { $txId, clearTxId } from "@vega-api/custom/stores/txId.store";
import { LocalStorageService } from "@vega-core/service/LocalStorage.service";
import { getVersion } from "@vega-core/utils/appVersion";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

export const LOGIN_PATH = "/login";
const ACTIVATE_PATH = "/activate";

export const BFF_LOGOUT_PATH = "/api/authentication/v1/oauth2/logout";

function reportMissingTxIdToFaro() {
  if (isFalsy($txId.get())) {
    faro.api.pushError(new Error("Logout initiated without TXID"), {
      type: "missing-txid-on-logout",
      context: {
        path: window.location.pathname,
        timestamp: new Date().toISOString(),
      },
    });
  }
}

export function clearSessionState() {
  reportMissingTxIdToFaro();

  QueryClientInstance.setDefaultOptions({ queries: { enabled: false } });
  QueryClientInstance.cancelQueries();
  QueryClientInstance.clear();
  window.localStorage.clear();
  window.sessionStorage.clear();
  clearTxId();

  const appVersion = getVersion();

  if (isTruthy(appVersion)) {
    LocalStorageService.setAppVersion(appVersion);
  }
}

export function isOnLoginPage(): boolean {
  return window.location.pathname.startsWith(LOGIN_PATH);
}

let logoutPromise: Promise<void> | null = null;
let logoutFlag = false;

export function setLogoutInProgress(value: boolean) {
  logoutFlag = value;
}

export function getLogoutInProgress(): boolean {
  return logoutFlag || logoutPromise !== null;
}

async function executeLogout(beforeRedirect?: () => Promise<void> | void) {
  if (logoutPromise) return logoutPromise;

  if (isOnLoginPage()) return;

  logoutPromise = (async () => {
    try {
      clearSessionState();
      await beforeRedirect?.();

      if (!matchPath(ACTIVATE_PATH, window.location.pathname)) {
        window.location.href = LOGIN_PATH;
      }
    } finally {
      setTimeout(() => {
        logoutPromise = null;
        logoutFlag = false;
      }, 1000);
    }
  })();

  return logoutPromise;
}

export async function handleLogout() {
  return executeLogout();
}

export async function handleBffLogout({
  sessionExpired = false,
}: { sessionExpired?: boolean } = {}) {
  if (logoutPromise) return logoutPromise;

  if (isOnLoginPage()) return;

  logoutPromise = (async () => {
    try {
      clearSessionState();

      if (matchPath(ACTIVATE_PATH, window.location.pathname)) return;

      window.location.href = sessionExpired ? LOGIN_PATH : BFF_LOGOUT_PATH;
    } finally {
      setTimeout(() => {
        logoutPromise = null;
        logoutFlag = false;
      }, 1000);
    }
  })();

  return logoutPromise;
}
